import { useRef, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { ScanInput } from 'shared/components/scan-input'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { useAuthStore } from 'shared/store/auth'
import { DamageDialog } from 'shared/components/damage-act'
import { CompactTable, SectionTitle } from 'shared/components/section'
import { ToneBanner } from 'shared/components/tone-banner'

/**
 * «От водителя» — the hub takes the return flow from the driver (ADR-0037, spec §12;
 * ticket 42). ONE scan per Parcel, no weight: it is the hand-over «водитель → хаб»,
 * and the answer says at once where to carry it, large and in its colour — «→ пункт»
 * green (it is already in that Point's open Bag), «→ полка партнёра X» yellow, the hub
 * shelf yellow with why, a refusal red. The Parcel just scanned may be recorded
 * damaged, with a comment, in a dialog. Below, what was scanned on this visit and
 * where each went — kept on the screen only. The scan is the hub worker's, at their
 * own hub.
 *
 * `fromDriver` is injected so the behaviour is tested without a backend.
 */
export type FromDriverAnswer = RouterOutputs['returnFlow']['FromDriver']
export type FromDriverRequest = RouterInputs['returnFlow']['FromDriver']

const fromDriverViaTrpc = (input: FromDriverRequest) => trpcClient.returnFlow.FromDriver.mutate(input)

/** One scan of this visit, as the screen remembers it. */
type Scanned = { id: number; answer: FromDriverAnswer; title: string }

export function HubFromDriver({
	fromDriver = fromDriverViaTrpc,
}: {
	fromDriver?: (input: FromDriverRequest) => Promise<FromDriverAnswer>
}) {
	const { t } = useTranslate()
	const words = useFromDriverWords()
	const role = useAuthStore((s) => s.user?.role)
	const [answer, setAnswer] = useState<FromDriverAnswer | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [sending, setSending] = useState(false)
	const [round, setRound] = useState(0)
	const [scanned, setScanned] = useState<Scanned[]>([])
	const ids = useRef(0)

	if (role && role !== 'hub') {
		return (
			<p className='text-sm text-muted-foreground' data-testid='hub-from-driver-hub-only'>
				{t('hubFromDriver.hubOnly')}
			</p>
		)
	}

	const onScan = async (code: string) => {
		if (sending) return
		setSending(true)
		setError(null)
		try {
			const res = await fromDriver({ code })
			setAnswer(res)
			const id = ids.current++
			setScanned((prev) => [{ id, answer: res, title: words(res).title }, ...prev].slice(0, 100))
			setRound((n) => n + 1)
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e))
		} finally {
			setSending(false)
		}
	}

	const current = answer ? words(answer) : null

	return (
		<div className='flex flex-col gap-8' data-testid='hub-from-driver'>
			<section className='flex flex-col gap-3'>
				<ScanInput key={round} onScan={onScan} disabled={sending} />
				{error && (
					<p role='alert' className='text-sm text-destructive' data-testid='hub-from-driver-error'>
						{t('hubFromDriver.failed', { message: error })}
					</p>
				)}
				{answer && current ? (
					<ToneBanner
						tone={answer.tone}
						title={current.title}
						detail={current.detail}
						testId='hub-from-driver-answer'
						titleTestId='hub-from-driver-title'
						detailTestId='hub-from-driver-detail'
						attributes={{ 'data-outcome': answer.outcome }}
						actions={
							answer.outcome !== 'refused' && (
								<DamageDialog
									key={answer.parcelId}
									parcelId={answer.parcelId}
									code={answer.trackingCode}
									testId='hub-from-driver-damage'
									onClosed={() => setRound((n) => n + 1)}
								/>
							)
						}
					/>
				) : (
					<p className='text-xs text-muted-foreground'>{t('hubFromDriver.hint')}</p>
				)}
			</section>

			{scanned.length > 0 && (
				<section className='flex flex-col gap-2'>
					<SectionTitle title={t('hubFromDriver.scannedTitle')} count={scanned.length} />
					<CompactTable testId='hub-from-driver-scanned'>
						<TableHeader>
							<TableRow>
								<TableHead>{t('hub.shelfCode')}</TableHead>
								<TableHead>{t('hubFromDriver.colWhere')}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{scanned.map((s) => (
								<TableRow key={s.id}>
									<TableCell className='font-mono'>{s.answer.trackingCode}</TableCell>
									<TableCell
										className={cn(
											'font-medium',
											s.answer.tone === 'ready' && 'text-emerald-700 dark:text-emerald-400',
											s.answer.tone === 'attention' && 'text-amber-700 dark:text-amber-400',
											s.answer.tone === 'refused' && 'text-destructive',
										)}
									>
										{s.title}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</CompactTable>
				</section>
			)}
		</div>
	)
}

/** The words of one answer — its headline and its detail — by `outcome`. */
function useFromDriverWords() {
	const { t } = useTranslate()
	return (answer: FromDriverAnswer): { title: string; detail: string } => {
		switch (answer.outcome) {
			case 'to_point':
				return {
					title: t('hubFromDriver.toPoint', { point: answer.pointCode }),
					detail: t('hubFromDriver.toPointDetail', { code: answer.trackingCode, bag: answer.bag.code }),
				}
			case 'to_partner_shelf':
				return { title: t('hubFromDriver.toPartnerShelf', { partner: answer.partnerName }), detail: answer.trackingCode }
			case 'to_hub_shelf':
				return {
					title: t('hubFromDriver.toHubShelf'),
					detail: [answer.trackingCode, ...answer.reasons.map((r) => t(`hubShelfReason.${r}`))].join(' · '),
				}
			case 'found':
				return {
					title: t('hubFound.title'),
					detail: t('hubFound.detail', { code: answer.trackingCode, partner: answer.partnerName, point: answer.pointCode }),
				}
			case 'refused':
				return {
					title: t('hubFromDriver.refused'),
					detail: `${answer.trackingCode}: ${t(`returnRefusal.${answer.reason}`)}`,
				}
		}
	}
}
